import { useMemo, useState } from 'react';
import { ChevronRight, Search } from 'lucide-react';
import { Checkbox } from '../../ui/checkbox';
import { Input } from '../../ui/input';
import { KIND_META } from '../qualityConfig';

export const itemKey = (item) => `${item.kind}|${item.ref_key}`;

const CatalogRow = ({ item, checked, onToggle }) => (
    <label
        className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors duration-150 ${checked ? 'border-violet-400 bg-violet-500/5 dark:border-violet-600' : 'hover:bg-muted/50'}`}
        data-testid={`catalog-item-${item.kind}-${item.ref_key}`}
    >
        <Checkbox checked={checked} onCheckedChange={() => onToggle(item)} className="mt-0.5" />
        <div className="min-w-0">
            <p className="text-sm font-medium leading-snug">{item.title}</p>
            <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{item.subtitle}</p>
        </div>
    </label>
);

const GroupHeader = ({ group, count, picked, allPicked, collapsible, open, onToggleOpen, onToggleAll }) => (
    <div className="sticky top-0 z-10 flex items-center justify-between gap-2 bg-background/95 py-1.5 backdrop-blur">
        <button type="button" onClick={onToggleOpen} disabled={!collapsible} className="flex min-w-0 items-center gap-1.5 text-left disabled:cursor-default">
            {collapsible && <ChevronRight className={`h-4 w-4 shrink-0 transition-transform duration-200 ${open ? 'rotate-90' : ''}`} />}
            <span className="truncate font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground">{group}</span>
            <span className="text-xs text-muted-foreground">({count})</span>
            {picked > 0 && <span className="rounded-full bg-violet-600 px-1.5 text-[10px] font-semibold text-white">{picked}</span>}
        </button>
        <button type="button" onClick={onToggleAll} className="shrink-0 text-xs font-medium text-violet-600 hover:underline dark:text-violet-300">
            {allPicked ? 'Clear' : 'Select all'}
        </button>
    </div>
);

const groupItems = (items, query) => {
    const needle = query.trim().toLowerCase();
    const map = new Map();
    items.forEach((it) => {
        if (needle && !`${it.title} ${it.group} ${it.subtitle}`.toLowerCase().includes(needle)) return;
        if (!map.has(it.group)) map.set(it.group, []);
        map.get(it.group).push(it);
    });
    return [...map.entries()];
};

export const CatalogPicker = ({ kind, items, selected, onToggle, onSetMany }) => {
    const [query, setQuery] = useState('');
    const [openGroups, setOpenGroups] = useState(() => new Set());
    const collapsible = kind === 'job_simulation';
    const groups = useMemo(() => groupItems(items, query), [items, query]);
    const toggleOpen = (g) => setOpenGroups((s) => { const n = new Set(s); n.has(g) ? n.delete(g) : n.add(g); return n; });

    return (
        <div className="flex h-full min-h-0 flex-col">
            <div className="relative px-4 pt-3">
                <Search className="pointer-events-none absolute left-7 top-1/2 mt-1.5 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={collapsible ? 'Search skills or lessons...' : `Search ${KIND_META[kind].label}...`}
                    className="pl-9" data-testid={`catalog-search-${KIND_META[kind].testId}`} />
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3" data-testid={`catalog-list-${KIND_META[kind].testId}`}>
                {groups.length === 0 && <p className="py-10 text-center text-sm text-muted-foreground">Nothing matches “{query}”.</p>}
                {groups.map(([group, list]) => {
                    const picked = list.filter((it) => selected[itemKey(it)]).length;
                    const open = !collapsible || Boolean(query.trim()) || openGroups.has(group);
                    return (
                        <section key={group} className="mb-3">
                            <GroupHeader group={group} count={list.length} picked={picked} allPicked={picked === list.length} collapsible={collapsible}
                                open={open} onToggleOpen={() => toggleOpen(group)} onToggleAll={() => onSetMany(list, picked !== list.length)} />
                            {open && (
                                <div className="mt-1 grid gap-2 sm:grid-cols-2">
                                    {list.map((it) => <CatalogRow key={it.ref_key} item={it} checked={Boolean(selected[itemKey(it)])} onToggle={onToggle} />)}
                                </div>
                            )}
                        </section>
                    );
                })}
            </div>
        </div>
    );
};
